import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Plus, 
  Snowflake, 
  Sun,
  Settings, 
  ChevronLeft, 
  ChevronRight,
  CreditCard
} from 'lucide-react'
import { useStore } from '@/store'
import VirtualCard from '@/components/VirtualCard'
import Button from '@/components/ui/Button'
import { toast } from 'sonner'
import { formatCurrency } from '@/lib/utils'

export default function Cards() {
  const cards = useStore(state => state.cards)
  const freezeCard = useStore(state => state.freezeCard)
  const unfreezeCard = useStore(state => state.unfreezeCard)
  
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showDetails, setShowDetails] = useState(false)
  
  const currentCard = cards[currentIndex]
  
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? cards.length - 1 : prev - 1))
  }
  
  const handleNext = () => {
    setCurrentIndex((prev) => (prev === cards.length - 1 ? 0 : prev + 1))
  }
  
  const handleFreezeToggle = () => {
    if (currentCard.status === 'active') {
      freezeCard(currentCard.id)
      toast.success('Card frozen successfully')
    } else {
      unfreezeCard(currentCard.id)
      toast.success('Card unfrozen successfully')
    }
  }
  
  const handleAddCard = () => {
    toast.info('Add card feature coming soon!')
  }
  
  const spendingProgress = (currentCard.monthlySpent / currentCard.spendingLimit) * 100
  
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">My Cards</h1>
        <Button variant="primary" size="sm" onClick={handleAddCard}>
          <Plus className="w-4 h-4 mr-1" />
          Add Card
        </Button>
      </div>
      
      {/* Card Carousel */}
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentCard.id}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
          >
            <VirtualCard card={currentCard} isActive={true} />
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation arrows */}
        {cards.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-10 h-10 rounded-full bg-background-elevated border border-border flex items-center justify-center hover:bg-background-hover transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-10 h-10 rounded-full bg-background-elevated border border-border flex items-center justify-center hover:bg-background-hover transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
      
      {/* Card dots */}
      {cards.length > 1 && (
        <div className="flex justify-center gap-2">
          {cards.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentIndex 
                  ? 'w-6 bg-brand' 
                  : 'bg-border hover:bg-border-hover'
              }`}
            />
          ))}
        </div>
      )}
      
      {/* Card Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 rounded-2xl bg-background-elevated border border-border"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-text-secondary">Card Type</p>
            <p className="font-medium capitalize">{currentCard.type} • {currentCard.tier}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-text-secondary">Status</p>
            <span className={`inline-flex items-center gap-1 font-medium ${
              currentCard.status === 'active' ? 'text-accent-green' : 'text-accent-red'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                currentCard.status === 'active' ? 'bg-accent-green' : 'bg-accent-red'
              }`} />
              {currentCard.status === 'active' ? 'Active' : 'Frozen'}
            </span>
          </div>
        </div>
        
        {/* Spending limit */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">Monthly Spending</span>
            <span className="font-medium">
              {formatCurrency(currentCard.monthlySpent)} / {formatCurrency(currentCard.spendingLimit)}
            </span>
          </div>
          <div className="h-2 bg-background rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-brand to-brand-light rounded-full transition-all duration-500"
              style={{ width: `${Math.min(spendingProgress, 100)}%` }}
            />
          </div>
          <p className="text-xs text-text-tertiary">
            {spendingProgress.toFixed(1)}% of monthly limit used
          </p>
        </div>
      </motion.div>
      
      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button 
          variant={currentCard.status === 'active' ? 'secondary' : 'primary'}
          onClick={handleFreezeToggle}
        >
          {currentCard.status === 'active' ? (
            <>
              <Snowflake className="w-5 h-5 mr-2" />
              Freeze Card
            </>
          ) : (
            <>
              <Sun className="w-5 h-5 mr-2" />
              Unfreeze Card
            </>
          )}
        </Button>
        <Button variant="secondary">
          <Settings className="w-5 h-5 mr-2" />
          Card Settings
        </Button>
      </div>
      
      {/* All Cards List */}
      <section>
        <h2 className="text-lg font-semibold mb-4">All Cards</h2>
        <div className="space-y-3">
          {cards.map((card, index) => (
            <motion.button
              key={card.id}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => setCurrentIndex(index)}
              className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all ${
                index === currentIndex
                  ? 'bg-brand/10 border-brand/50'
                  : 'bg-background-elevated border-border hover:border-border-hover'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${card.color}-500 to-${card.color}-600 flex items-center justify-center`}>
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-medium capitalize">{card.type} Card</p>
                  <p className="text-sm text-text-tertiary">
                    •••• {card.lastFour}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${
                  card.status === 'active' ? 'bg-accent-green' : 'bg-accent-red'
                }`} />
                {card.isDefault && (
                  <span className="text-xs text-brand bg-brand/10 px-2 py-1 rounded-full">
                    Default
                  </span>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </div>
  )
}